import { HeartbeatData } from '@kentik/ui-shared/types/agentHeartbeat.types';

export interface HeartbeatState {
  isAgentHeartbeatAlive: boolean;
  isThinking?: boolean;
}

type SetHeartbeatState = (state: HeartbeatState) => void;

/**
 * Tracks the freshness of server-sent agent heartbeats on the client. The
 * server publishes `agent_heartbeat` events carrying a TTL; if a fresh
 * heartbeat does not arrive before the TTL elapses the agent is considered no
 * longer working. State changes are pushed back to the owner via the
 * `setState` callback (typically a mobx model's `set`).
 */
export default class AgentHeartbeatTracker {
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly setState: SetHeartbeatState;

  constructor(setState: SetHeartbeatState) {
    this.setState = setState;
  }

  handle(data?: HeartbeatData): void {
    this.clear();

    const ttlMs = data?.ttl_ms ?? 0;
    const alive = data?.alive === true && ttlMs > 0;

    if (alive) {
      this.setState({ isAgentHeartbeatAlive: true });
      this.timer = setTimeout(() => {
        this.setState({ isAgentHeartbeatAlive: false, isThinking: false });
      }, ttlMs);
    } else {
      this.setState({ isAgentHeartbeatAlive: false, isThinking: false });
    }
  }

  clear(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
